import type { CSSProperties, SVGProps } from "react";
import type { CanonicalMembershipType } from "@dj/convex-api";

export type MembershipCrestSize =
  | "avatar-small"
  | "avatar-large"
  | "purchase"
  | "description";

export type MembershipCrestProps = Readonly<{
  ariaLabel?: string;
  className?: string;
  decorative?: boolean;
  membershipType: CanonicalMembershipType;
  size: MembershipCrestSize;
}>;

export const membershipDisplayNames: Readonly<
  Record<CanonicalMembershipType, string>
> = {
  community: "Community",
  pilot_operator: "Pilot Operator",
  pilot_professional: "Pilot Pro",
  drone_business: "Drone Business",
  enterprise: "Enterprise",
};

const crestPixels: Readonly<Record<MembershipCrestSize, number>> = {
  "avatar-small": 18,
  "avatar-large": 32,
  purchase: 28,
  description: 56,
};

export function isCanonicalMembershipType(
  value: string,
): value is CanonicalMembershipType {
  return value in membershipDisplayNames;
}

function FourPetalRotor({
  centerFill,
  petalFill,
  stroke,
  strokeWidth = 1.4,
}: Readonly<{
  centerFill: string;
  petalFill: string;
  stroke: string;
  strokeWidth?: number;
}>) {
  return (
    <g stroke={stroke} strokeWidth={strokeWidth} strokeLinejoin="round">
      <ellipse cx="32" cy="20" fill={petalFill} rx="4.3" ry="9" />
      <ellipse cx="44" cy="32" fill={petalFill} rx="9" ry="4.3" />
      <ellipse cx="32" cy="44" fill={petalFill} rx="4.3" ry="9" />
      <ellipse cx="20" cy="32" fill={petalFill} rx="9" ry="4.3" />
      <circle cx="32" cy="32" fill={centerFill} r="5" />
      <circle cx="32" cy="32" fill={stroke} r="2" stroke="none" />
    </g>
  );
}

function CompactRotor({
  cx,
  cy,
  fill,
  ring,
  compact,
}: Readonly<{
  cx: number;
  cy: number;
  fill: string;
  ring: string;
  compact: boolean;
}>) {
  return (
    <g transform={`translate(${cx} ${cy})`}>
      <circle
        cx="0"
        cy="0"
        fill={fill}
        r={compact ? 5.1 : 6.1}
        stroke={ring}
        strokeWidth="1.4"
      />
      {compact ? (
        <circle cx="0" cy="0" fill={ring} r="1.8" />
      ) : (
        <g fill={ring}>
          <ellipse cx="0" cy="-2.6" rx="1.15" ry="2.2" />
          <ellipse cx="2.6" cy="0" rx="2.2" ry="1.15" />
          <ellipse cx="0" cy="2.6" rx="1.15" ry="2.2" />
          <ellipse cx="-2.6" cy="0" rx="2.2" ry="1.15" />
          <circle cx="0" cy="0" fill={fill} r="0.8" />
        </g>
      )}
    </g>
  );
}

function CommunityCrest() {
  return (
    <>
      <circle
        cx="32"
        cy="32"
        fill="#d8dee7"
        r="29"
        stroke="#18212f"
        strokeWidth="2.2"
      />
      <circle
        cx="32"
        cy="32"
        fill="#f8fafc"
        r="24.5"
        stroke="#667085"
        strokeWidth="1.3"
      />
      <FourPetalRotor
        centerFill="#a8b0bd"
        petalFill="#c8ced8"
        stroke="#303846"
      />
    </>
  );
}

function PilotOperatorCrest() {
  return (
    <>
      <path
        d="M13 5h38l8 8v38l-8 8H13l-8-8V13z"
        fill="#d58a52"
        stroke="#2d1a12"
        strokeWidth="1.8"
      />
      <path
        d="M15 9h34l6 6v34l-6 6H15l-6-6V15z"
        fill="#3b2419"
        stroke="#f0b47d"
        strokeWidth="1.4"
      />
      <circle
        cx="32"
        cy="32"
        fill="none"
        r="17"
        stroke="#e69b62"
        strokeWidth="1.5"
      />
      <FourPetalRotor
        centerFill="#f0b47d"
        petalFill="#efa66f"
        stroke="#63351f"
        strokeWidth={1.25}
      />
    </>
  );
}

function PilotProfessionalCrest() {
  return (
    <>
      <path
        d="M32 1 63 32 32 63 1 32z"
        fill="#e8bd58"
        stroke="#4b3515"
        strokeWidth="1.5"
      />
      <path
        d="M32 6 58 32 32 58 6 32z"
        fill="#ffe2a0"
        stroke="#fff1c5"
        strokeWidth="1.2"
      />
      <path
        d="M32 10 54 32 32 54 10 32z"
        fill="#112a49"
        stroke="#b97c1e"
        strokeWidth="2"
      />
      <circle
        cx="32"
        cy="32"
        fill="none"
        r="16.5"
        stroke="#f3cc70"
        strokeWidth="1.6"
      />
      <FourPetalRotor
        centerFill="#fff0b8"
        petalFill="#f5ca67"
        stroke="#9f6817"
        strokeWidth={1.2}
      />
    </>
  );
}

function DroneBusinessCrest({ compact }: Readonly<{ compact: boolean }>) {
  return (
    <>
      <path
        d="M32 3 58 18v28L32 61 6 46V18z"
        fill="#cfd4dc"
        stroke="#18212c"
        strokeWidth="1.8"
      />
      <path
        d="M32 8 53 20v24L32 56 11 44V20z"
        fill="#262b34"
        stroke="#f1f4f8"
        strokeWidth="1.3"
      />
      <g fill="none" stroke="#9ca6b4" strokeWidth="1.5">
        <path d="m32 22-12 18M32 22l12 18M20 40h24" />
      </g>
      <CompactRotor
        compact={compact}
        cx={32}
        cy={22}
        fill="#353c48"
        ring="#f5f7fa"
      />
      <CompactRotor
        compact={compact}
        cx={20}
        cy={40}
        fill="#353c48"
        ring="#f5f7fa"
      />
      <CompactRotor
        compact={compact}
        cx={44}
        cy={40}
        fill="#353c48"
        ring="#f5f7fa"
      />
    </>
  );
}

function EnterpriseCrest({ compact }: Readonly<{ compact: boolean }>) {
  return (
    <>
      <path
        d="M13 3h38l10 10v38L51 61H13L3 51V13z"
        fill="#e0bd61"
        stroke="#261032"
        strokeWidth="1.7"
      />
      <path
        d="M15 7h34l8 8v34l-8 8H15l-8-8V15z"
        fill="#5a1975"
        stroke="#f4da8b"
        strokeWidth="1.6"
      />
      <path
        d="M18 11h28l7 7v28l-7 7H18l-7-7V18z"
        fill="#48105f"
        stroke="#b984c9"
        strokeWidth="1"
      />
      <g fill="none" stroke="#e8c86e" strokeWidth="1.35">
        <path d="m32 16-14 10 4 19h20l4-19-14-10z" />
        <path d="m18 26 28 0M22 45l24-19M42 45 18 26M22 45l10-29M42 45 32 16" />
      </g>
      <CompactRotor
        compact={compact}
        cx={32}
        cy={16}
        fill="#5a1975"
        ring="#fffaf0"
      />
      <CompactRotor
        compact={compact}
        cx={18}
        cy={26}
        fill="#5a1975"
        ring="#fffaf0"
      />
      <CompactRotor
        compact={compact}
        cx={46}
        cy={26}
        fill="#5a1975"
        ring="#fffaf0"
      />
      <CompactRotor
        compact={compact}
        cx={22}
        cy={45}
        fill="#5a1975"
        ring="#fffaf0"
      />
      <CompactRotor
        compact={compact}
        cx={42}
        cy={45}
        fill="#5a1975"
        ring="#fffaf0"
      />
    </>
  );
}

function CrestArtwork({
  compact,
  membershipType,
}: Readonly<{
  compact: boolean;
  membershipType: CanonicalMembershipType;
}>) {
  switch (membershipType) {
    case "community":
      return <CommunityCrest />;
    case "pilot_operator":
      return <PilotOperatorCrest />;
    case "pilot_professional":
      return <PilotProfessionalCrest />;
    case "drone_business":
      return <DroneBusinessCrest compact={compact} />;
    case "enterprise":
      return <EnterpriseCrest compact={compact} />;
  }
}

export function MembershipCrest({
  ariaLabel,
  className,
  decorative = false,
  membershipType,
  size,
}: MembershipCrestProps) {
  const pixels = crestPixels[size];
  const label =
    ariaLabel ?? `${membershipDisplayNames[membershipType]} membership`;
  const style: CSSProperties = {
    display: "block",
    flex: "0 0 auto",
    height: pixels,
    width: pixels,
  };
  const accessibilityProps: SVGProps<SVGSVGElement> = decorative
    ? { "aria-hidden": true }
    : { "aria-label": label, role: "img" };

  return (
    <svg
      {...accessibilityProps}
      className={className}
      data-membership={membershipType}
      data-membership-crest="true"
      data-size={size}
      style={style}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
    >
      {decorative ? null : <title>{label}</title>}
      <CrestArtwork
        compact={size === "avatar-small"}
        membershipType={membershipType}
      />
    </svg>
  );
}
